import { Check, X } from 'lucide-react';
import { useMemo } from 'react';
import { buildPasswordRules, type PasswordPolicy } from '../../lib/passwordRules';
import { usePasswordPolicy } from '../../hooks/usePasswordPolicy';

interface Props {
    /** Valor actual de la contraseña que se está escribiendo (undefined en el primer render de RHF). */
    value: string | undefined;
    /**
     * Policy efectiva (del backend, vía `usePasswordPolicy`). Se pasa desde el form para compartir
     * una sola lectura del hook con el schema zod. Si se omite, el checklist la trae por su cuenta
     * (con fallback) — útil para usos sueltos.
     */
    policy?: PasswordPolicy;
}

/**
 * Checklist en vivo de las reglas de complejidad de la contraseña.
 * Las reglas se derivan de la policy (DEC-RBAC-052): el front refleja lo que exige el backend.
 * Marca en verde las reglas que cumple la contraseña a medida que el usuario escribe.
 */
export function PasswordRulesChecklist({ value, policy }: Props) {
    // Solo lee el hook si el form no pasó la policy (evita una 2ª suscripción cuando sí la pasa;
    // el hook cachea con staleTime Infinity, así que igual sería barato, pero esto es más limpio).
    const fallbackPolicy = usePasswordPolicy().policy;
    const effective = policy ?? fallbackPolicy;
    const rules = useMemo(() => buildPasswordRules(effective), [effective]);

    return (
        <ul className="space-y-1 pt-1">
            {rules.map((r) => {
                const ok = r.test(value ?? '');
                return (
                    <li
                        key={r.label}
                        className={`flex items-center gap-1.5 text-xs ${
                            ok ? 'text-success' : 'text-muted-foreground'
                        }`}
                    >
                        {ok ? (
                            <Check className="size-3.5 shrink-0" />
                        ) : (
                            <X className="size-3.5 shrink-0" />
                        )}
                        {r.label}
                    </li>
                );
            })}
        </ul>
    );
}
